/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DisconnectReason } from './DisconnectReason';
import { LogProvider, TokenResult } from './Interfaces';
import { IMessageLossIndicator, ITrouterRequest, UserActivityEventReason } from './InternalInterfaces';
import { Logger } from './Logger';
import { usedProtocol, usedProtocolAfterFallback } from './ProtocolAdapters';
import { IReconnectParameters, ITrouterReconnectEventArgs, ReconnectReason } from './TrouterConnection';

export type FallbackReason = { kind: 'redirect', host: string } | { kind: 'redirect-no-host' } | { kind: 'v4c-websocket-failure' };

export interface IWorker {
    getToken(useCache: boolean, backoff: boolean, claimsChallenge: string | undefined, reason?: FallbackReason): void;

    startConnectionTimer(): void;
    stopConnectionTimer(): void;
    startPingTimer(): void;
    stopPingTimer(): void;

    /** Returns true if config mandates to not register connection URL with EDF Registrar */
    shouldSkipRegistration(): boolean;
    /** Returns true if user specified a custom TTL for registration */
    hasCustomRegistrationTtl(): boolean;
    startRegistrationTimer(): void;
    startRegistrationRetryTimer(): void;
    stopRegistrationTimer(): void;

    startSocketIo(token: TokenResult): void;
    stopSocketIo(): void;
    isIncallMode(): boolean;
    restartIncallModeTimer(): void;
    enterIncallMode(): void;
    exitIncallMode(): void;

    dispatchConnected(): void;
    dispatchRegistered(): void;
    dispatchUnregistered(): void;
    dispatchDownstreamRequest(request: ITrouterRequest): void;
    /** Start reconnecting due to socket error */
    dispatchReconnecting(): void;
    /** Reconnection is requested by server (current connection is still valid) */
    dispatchReconnectIsRequired(useConnectParamsFromCache: boolean, reason: ReconnectReason): void;
    dispatchDisconnected(): void;
    dispatchTerminalError(): void;
    dispatchTrouterMessageLost(flowTags: IMessageLossIndicator[]): void;
    /** Notify the connection that the client has failed to connect. */
    countDisconnectBeforeConnectionEstablishment(): void;

    sendAllocateRequest(token: TokenResult): void;
    sendPingRequest(): void;

    connectV4c(token: TokenResult, reason?: FallbackReason): void;
    sendV4cAuthenticationEvent(token: TokenResult): void;

    sendUserActivityState(reason: UserActivityEventReason, connected: boolean, correlationVector?: string): void;

    sendRegisterRequest(): void;
    sendUnregisterRequest(): void;

    /** Reset planned token request (if any) and backoff interval */
    resetTokenBackoff(): void;
    /** Cancel pending registration requests (if any) */
    cancelPendingRegistrationRequests(): void;
    /** Cancel all pending downstream events related timers. */
    clearSentEventTimers(): void;
    sendDisconnectTelemetryEvent(error?: string): void;
}

export enum State {
    Initial,
    /** waiting for token from User */
    RetrievingToken,
    /** waiting for allocation response from service */
    Allocating,
    /**
     * waiting for Socket.IO handshake to be finished. After that Socket.IO will
     * start to try transports and send 'connecting' event
     */
    Handshaking,
    /**
     * waiting for 'connected' event - socket.io is trying different transports.
     * onSocketError event should be ignored, as it's expected event. So, only ConnectFailed or
     * connection timeout meas failed connection
     */
    Connecting,
    /**
     * connecting to a websocket /v4/c URL
     *
     * this is the equivalent of the "Allocating -> Handshaking -> Connecting" chain
     * from the /v4/a protocol
     */
    AnonymousConnecting,
    /**
     * connected to a websocket /v4/c URL, sending the authentication message,
     * and waiting to receive a `trouter.connected` event back
     */
    WebsocketAuthenticating,
    /** ready to work */
    Connected,
    /** waiting for a successful 'unregister' response */
    Unregistering,
    /**
     * Unrecoverable state
     * Trouter cannot start once in this state
     */
    TerminalError
}

enum RegistrationState {
    Initial,
    /** Waiting for a successful 'register' response */
    Registering,
    /** like the above, but another registration must happen when done because of resendRegistration() */
    RegisteringButResendPending,
    /** Registration failed, waiting to retry */
    Retrying,
    /** Registration succeeded */
    Registered,
    /** Registration is skipped */
    RegistrationDisabled
}

type ReconnectOptions = { backoff: boolean, allowCachedToken: boolean, claimsChallenge?: string, fallbackReason?: FallbackReason };

/**
 * State machine for trouter
 */
export class TrouterFsm {
    private logger: Logger;
    private state: State = State.Initial;
    private registrationState: RegistrationState;
    private autoReconnect = true;

    constructor(
        logfunc: LogProvider,
        private worker: IWorker,
        private incallModeEnabled: boolean,
        private protocolSelector: typeof usedProtocol
    ) {
        this.logger = new Logger('ConnectionFsm', logfunc);
        this.registrationState = RegistrationState.Initial;
    }

    /** Returns current state */
    public getState(): State {
        return this.state;
    }

    public isActive(): boolean {
        return this.state === State.Allocating
            || this.state === State.Connected
            || this.state === State.Handshaking
            || this.state === State.Connecting
            || this.state === State.RetrievingToken
            || this.state === State.AnonymousConnecting
            || this.state === State.WebsocketAuthenticating;
    }

    public isConnecting(): boolean {
        return this.state === State.Allocating
            || this.state === State.Handshaking
            || this.state === State.Connecting
            || this.state === State.AnonymousConnecting;
    }

    //////////// Events ////////////
    /**
     * Start trouter
     */
    public start(): boolean {
        if (this.state === State.Initial) {
            this.setState(State.RetrievingToken);
            this.worker.getToken(true, false, undefined);

            return true;
        } else {
            this.showIgnored('start');

            return false;
        }
    }

    /**
     * Stop trouter
     * @param dontUnregister    if true, FSM will not unregister. It's needed in case of two simultaneous
     *                          connections, to replace first connection with second - registrar just updates
     *                          the existing record for the user, by registrationId (see their API docs),
     *                          that id does not change during the lifetime of a TrouterService instance.
     * @param isTerminalError   if true, FSM will transition into the terminal state
     */
    public stop(
       dontUnregister: boolean,
       isTerminalError?: boolean,
    ) {
        if (dontUnregister) {
            this.registrationState = RegistrationState.Initial;
        }

        if (this.worker.isIncallMode()) {
            this.worker.exitIncallMode();
        }

        this.worker.resetTokenBackoff();
        this.worker.cancelPendingRegistrationRequests();
        this.worker.stopConnectionTimer();
        this.worker.stopPingTimer();
        this.worker.clearSentEventTimers();
        this.worker.stopRegistrationTimer();
        this.worker.stopSocketIo();

        if (this.state === State.Connected) {
            this.worker.sendDisconnectTelemetryEvent('connection stopped');
        }

        // Stop can happen in any state - the only difference is if registration exists or not.
        // It can be also called a second time, just to stop a stuck unregistration.
        if ((this.registrationState === RegistrationState.Registered
             || this.registrationState === RegistrationState.Registering
             || this.registrationState === RegistrationState.RegisteringButResendPending)
            && this.state !== State.Unregistering
        ) {
            this.registrationState = RegistrationState.Initial;
            this.setState(State.Unregistering);
            this.worker.sendUnregisterRequest();
        } else {
            if (isTerminalError) {
                this.setState(State.TerminalError);
                this.worker.dispatchTerminalError();
            } else {
                this.setState(State.Initial);
                this.worker.dispatchDisconnected();
            }
        }
    }

    public onTokenReceived(token: TokenResult, reason?: FallbackReason, reconnectParams?: IReconnectParameters) {
        if (this.state === State.RetrievingToken) {
            if (usedProtocolAfterFallback(this.protocolSelector(token.tokenType, reconnectParams), reason) === 'v4c') {
                this.setState(State.AnonymousConnecting);
                this.worker.startConnectionTimer();
                this.worker.connectV4c(token, reason);
            } else {
                this.setState(State.Allocating);
                this.worker.startConnectionTimer();
                this.worker.sendAllocateRequest(token);
            }
        } else {
            this.showIgnored('onTokenReceived');
        }
    }

    /**
     * if disconnectDetected - need to send ping to check if connection is really dead,
     * otherwise - network is available
     */
    public checkConnection(disconnectDetected: boolean) {
        if (disconnectDetected) {
            this.onPingInterval();
        }
    }

    /** Allocation request succeed with 200 */
    public onAllocationSucceed(token: TokenResult): boolean {
        // Call to dispatchUnregistered a little bit earlier takes into account the case when the state
        // of the state machine can change inside the callback, for example if Trouter.stop() is called inside the callback.
        if (this.state === State.Allocating && this.registrationState === RegistrationState.Registered) {
            this.worker.dispatchUnregistered();
        }

        if (this.state === State.Allocating) {
            this.setState(State.Handshaking);
            this.registrationState = RegistrationState.Initial;
            this.worker.startSocketIo(token);

            return true;
        } else {
            this.showIgnored('onAllocationSucceed');

            return false;
        }
    }

    /** Allocation request failed */
    public onAllocationFailed(unauthorized: boolean, claimsChallenge: string | undefined) {
        if (this.state === State.Allocating) {
            this.cleanUpAndInitiateReconnect({ backoff: true, allowCachedToken: !unauthorized, claimsChallenge });
        } else {
            this.showIgnored('onAllocationFailed');
        }
    }

    public onV4cException() {
        if (this.state === State.AnonymousConnecting || this.state === State.WebsocketAuthenticating) {
            this.logger.error('v4c exception, falling back to longpoll');
            this.cleanUpAndInitiateReconnect({ backoff: true, allowCachedToken: true, fallbackReason: { kind: 'v4c-websocket-failure' }});
        }
    }

    /** Connection didn't succeed in given timeout */
    public onConnectingTimeout() {
        // implementation note: this also called from onConnectingFailed
        if (this.state === State.Allocating
            || this.state === State.Connecting
            || this.state === State.Handshaking
            || this.state === State.AnonymousConnecting
            || this.state === State.WebsocketAuthenticating
        ) {
            this.cleanUpAndInitiateReconnect({ backoff: false, allowCachedToken: true });
        } else {
            this.showIgnored('onConnectingTimeout');
        }
    }

    /** socket.io handshake succeed and socket.io is trying next transport */
    public onConnecting() {
        if (this.state === State.Handshaking) {
            this.setState(State.Connecting);
        } else {
            this.showIgnored('onConnecting');
        }
    }

    /** socket.io connected (1::), but trouter.connected event has not arrived yet */
    public onSocketConnect(token: TokenResult) {
        if (this.state === State.AnonymousConnecting) {
            this.setState(State.WebsocketAuthenticating);
            this.worker.sendV4cAuthenticationEvent(token);
        } else {
            this.showIgnored('onSocketConnect');
        }
    }

    /** Socket.io connection wasn't established */
    public onConnectingFailed() {
        // This callback will be called when all transports failed to connect, i.e. socket.io can't proceed
        if (this.state === State.Connecting) {
            this.onConnectingTimeout();
        } else if (this.state === State.Handshaking) {
            // It should never happen, because socket.io should try at least one transport
            this.logger.error('Unexpected error in Socket.io - no valid transports');
            this.onConnectingTimeout();
        } else if (this.state === State.AnonymousConnecting || this.state === State.WebsocketAuthenticating) {
            this.logger.info('/v4/c falling back to longpoll');
            this.cleanUpAndInitiateReconnect({ backoff: true, allowCachedToken: true, fallbackReason: { kind: 'v4c-websocket-failure' }});
        } else {
            this.showIgnored('onConnectingFailed');
        }
    }

    /** Socket.io error (or disconnect) */
    public onSocketDisconnect(disconnectReason?: DisconnectReason) {
        if (this.state === State.Handshaking || this.state === State.Connected || this.state === State.WebsocketAuthenticating) {
            if (this.state === State.Connected) {
                this.worker.sendDisconnectTelemetryEvent(disconnectReason?.toTelemetryString());
            }
            if (this.state === State.WebsocketAuthenticating) {
                this.worker.countDisconnectBeforeConnectionEstablishment();
            }
            if (disconnectReason?.reason === 'skypetoken-deprecated') {
                this.logger.error('Skypetoken deprecated response, not retrying any further');
                this.onTerminalError();
            } else {
                this.cleanUpAndInitiateReconnect({
                    // If client was already successfully connected, no backoff is needed (we want to reconnect quickly).
                    // If however connection failed before the `trouter.connected` event arrived,
                    // that means some sort of error where we don't want to retry immediately.
                    backoff: this.state !== State.Connected && disconnectReason?.reason !== 'dup',
                    allowCachedToken: disconnectReason?.reason !== 'unauthorized',
                    claimsChallenge: disconnectReason?.claims});
            }
        } else {
            // Disconnect can be called because of an error.
            // Such error can happen in Connecting state, which means that
            // websocket transport failed to establish connection
            this.showIgnored('onSocketDisconnect');
        }
    }

    /** Received 'trouter.connected' event from service */
    public onTrouterConnected() {
        if (this.state === State.Connecting || this.state === State.WebsocketAuthenticating) {
            this.setState(State.Connected);
            this.worker.resetTokenBackoff();
            this.worker.stopConnectionTimer();
            this.worker.sendUserActivityState(UserActivityEventReason.Connected, true);
            this.worker.startPingTimer();
            this.worker.dispatchConnected();
            if (this.worker.shouldSkipRegistration()) {
                this.registrationState = RegistrationState.RegistrationDisabled;
                this.worker.dispatchRegistered();
            } else {
                this.registrationState = RegistrationState.Registering;
                this.worker.sendRegisterRequest();
            }
        } else {
            this.showIgnored('onTrouterConnected');
        }
    }

    /**
     * Received 'trouter.reconnect' event from service, or URL in configuration has changed,
     * need to establish a new connection
     */
    public onReconnectRequired(useConnectParamsFromCache: boolean, reason: ReconnectReason, eventArgs?: ITrouterReconnectEventArgs) {
        if (this.state === State.AnonymousConnecting || this.state === State.WebsocketAuthenticating) {
            if (eventArgs === undefined || eventArgs.target !== 'host' || eventArgs.url === undefined || eventArgs.url === '') {
                this.logger.error(`unexpected reconnect arguments: ${eventArgs?.target} ${eventArgs?.url}, reconnecting without cache`);
                this.cleanUpAndInitiateReconnect({ backoff: true, allowCachedToken: true, fallbackReason: { kind: 'redirect-no-host' }});
            } else {
                this.cleanUpAndInitiateReconnect({ backoff: true, allowCachedToken: true, fallbackReason: { kind: 'redirect', host: eventArgs.url }});
            }
        } else {
            this.worker.dispatchReconnectIsRequired(useConnectParamsFromCache, reason);
        }
    }

    public disableAutoReconnect() {
        this.autoReconnect = false;
    }

    /** Request from downstream is received */
    public onDownstreamRequest(request: ITrouterRequest) {
        if (this.state === State.Connected) {
            this.switchToIncallModeIfEnabled();
            this.worker.dispatchDownstreamRequest(request);
        } else {
            this.showIgnored('onDownstreamRequest');
        }
    }

    public onTrouterMessageLost(messageLossIndicators: IMessageLossIndicator[]) {
        if (this.state === State.Connected) {
            this.worker.dispatchTrouterMessageLost(messageLossIndicators);
        } else {
            this.showIgnored('onTrouterMessageLost');
        }
    }

    /** Timer for ping request fired */
    public onPingInterval() {
        if (this.state === State.Connected) {
            this.worker.sendPingRequest();
        } else {
            this.showIgnored('onPingInterval');
        }
    }

    /** No pong response from service within timeout */
    public onPingResponseTimeout() {
        this.onMissedResponse('onPingResponseTimeout');
    }

    /** Pong response */
    public onPingResponse() {
        if (this.state === State.Connected) {
            // do nothing
        } else {
            this.showIgnored('onPingResponse');
        }
    }

    public onRegistrationFailed() {
        if (this.state === State.Connected && this.registrationState === RegistrationState.Registering) {
            this.worker.dispatchUnregistered();
            this.registrationState = RegistrationState.Retrying;
            this.worker.startRegistrationRetryTimer();
        } else if (this.state === State.Connected && this.registrationState === RegistrationState.RegisteringButResendPending) {
            this.registrationState = RegistrationState.Registering;
            this.worker.sendRegisterRequest();
        } else {
            this.showIgnored('onRegistrationFailed');
        }
    }

    public onRetryRegistration() {
        if (this.state === State.Connected && this.registrationState === RegistrationState.Retrying) {
            this.registrationState = RegistrationState.Registering;
            this.worker.sendRegisterRequest();
        } else {
            this.showIgnored('onRetryRegistration');
        }
    }

    public onRegistrationSucceeded() {
        if (this.state === State.Connected && this.registrationState === RegistrationState.Registering) {
            this.registrationState = RegistrationState.Registered;
            this.worker.dispatchRegistered();
            this.worker.startRegistrationTimer();
        } else if (this.state === State.Connected && this.registrationState === RegistrationState.RegisteringButResendPending) {
            this.registrationState = RegistrationState.Registering;
            this.worker.sendRegisterRequest();
        } else {
            this.showIgnored('onRegistrationSucceeded');
        }
    }

    /** Registration TTL is about to expire */
    public onRegistrationNearExpiry() {
        if (this.state === State.Connected && this.registrationState === RegistrationState.Registered) {
            this.registrationState = RegistrationState.Registering;
            this.worker.sendRegisterRequest();
        } else {
            this.showIgnored('onRegistrationNearExpiry');
        }
    }

    public onUnregistrationDone() {
        if (this.state === State.Unregistering) {
            this.setState(State.Initial);
            this.worker.dispatchUnregistered();
            this.worker.dispatchDisconnected(); // TODO: only sending "disconneted" upon unregistration makes no sense
        } else {
            this.showIgnored('onUnregistrationDone');
        }
    }

    public onResendRegistration() {
        this.worker.dispatchUnregistered();

        if (this.state === State.Connected && this.registrationState === RegistrationState.Registered) {
            this.registrationState = RegistrationState.Registering;
            this.worker.stopRegistrationTimer();
            this.worker.sendRegisterRequest();
        } else if (this.state === State.Connected && this.registrationState === RegistrationState.Registering) {
            this.registrationState = RegistrationState.RegisteringButResendPending;
        }
    }

    public onIncallModeTimer() {
        this.worker.exitIncallMode();

        if (this.state === State.Connected) {
            this.worker.stopPingTimer();
            this.worker.startPingTimer();
        } else {
            this.showIgnored('onIncallModeTimer');
        }
    }

    public onSetNewUserActivityState() {
        this.worker.sendUserActivityState(UserActivityEventReason.Modified, this.state === State.Connected);
    }

    public onActivityStateResponseTimeout() {
        this.onMissedResponse('onActivityStateResponseTimeout');
    }

    public forceReconnect(reason: string) {
        if (this.state === State.Connected) {
            this.worker.sendDisconnectTelemetryEvent(reason);
        }
        this.worker.resetTokenBackoff();
        this.cleanUpAndInitiateReconnect({ backoff: false, allowCachedToken: true });
    }

    public onTerminalError() {
        this.logger.error(`Cannot proceed, reached terminal state. Switching from state '${State[this.state]}' to ${State[State.TerminalError]}`);
        this.stop(
            /* dontUnregister */ true,
            /* isTerminalError */ true
        );
        this.setState(State.TerminalError);
    }

    private onMissedResponse(onResponseHandlerName: string) {
        if (this.state === State.Connected) {
            this.worker.sendDisconnectTelemetryEvent(onResponseHandlerName);
            this.cleanUpAndInitiateReconnect({ backoff: false, allowCachedToken: true });
        } else {
            this.showIgnored(onResponseHandlerName);
        }
    }

    private showIgnored(event: string) {
        this.logger.debug(`Ignoring event '${event}' in state '${State[this.state]}'`);
    }

    private setState(state: State) {
        this.logger.info(`Switching from state '${State[this.state]}' to state '${State[state]}'`);
        if (this.state === state) {
            this.logger.error(`Attempt to switch to the current state '${State[state]}'`);

            return;
        }
        this.state = state;
    }

    private switchToIncallModeIfEnabled() {
        if (this.incallModeEnabled) {
            if (!this.worker.isIncallMode()) {
                this.worker.enterIncallMode();
                this.worker.stopPingTimer();
                this.worker.startPingTimer();
            }
            this.worker.restartIncallModeTimer();
        }
    }

    private cleanUpAndInitiateReconnect(options: ReconnectOptions)
    {
        if (!this.autoReconnect) {
            this.logger.info('Automatic reconnect is disabled, stopping this connection');
            this.stop(true);
            return;
        }

        this.worker.cancelPendingRegistrationRequests();
        this.worker.stopConnectionTimer();
        this.worker.stopPingTimer();
        this.worker.clearSentEventTimers();
        this.worker.stopRegistrationTimer();
        this.worker.stopSocketIo();

        this.setState(State.RetrievingToken);
        this.worker.dispatchReconnecting();
        this.worker.getToken(options.allowCachedToken, options.backoff, options.claimsChallenge, options.fallbackReason);
    }
}



// WEBPACK FOOTER //
// ./lib_temp/TrouterFsm.ts